import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Sparkles } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

interface Suggestion {
  value: string | null;
  confidence: number;
  rationale: string | null;
  source: string;
  cached: boolean;
}

interface Props {
  entity: 'ACCOUNT' | 'CONTACT' | 'LEAD';
  field: string;
  /**
   * Snapshot of the form values that should accompany the lookup. The
   * service uses these to render the prompt (e.g. company name to find
   * a website). Pass at minimum the disambiguation fields the prompt
   * needs.
   */
  context: Record<string, string | number | null | undefined>;
  /**
   * Called when the rep accepts the suggestion. Parent applies it to
   * the form field.
   */
  onAccept: (value: string) => void;
  className?: string;
  /**
   * Optional UI lock — pass true when the underlying field is empty
   * to nudge the rep ("the wand is here for a reason"). Has no
   * functional effect.
   */
  emphasize?: boolean;
}

/**
 * Epic 89, Story 89.4 — Field-level "fill from LLM" affordance.
 *
 * Sparkle button next to a form field. Clicking it sends the field
 * context to POST /lookup, then renders an inline popover with the
 * proposed value, a confidence score, and a one-sentence rationale.
 * Accepting fills the form field through `onAccept`.
 *
 * Failure modes the popover surfaces:
 *   402 → "Out of credits — ask your admin to top up"
 *   403 → "Smart lookup is disabled for this tenant"
 *   404 → (parent shouldn't have rendered the button; logged silently)
 *   5xx → "Lookup failed — try again"
 */
export function SuggestButton({
  entity,
  field,
  context,
  onAccept,
  className,
  emphasize = false,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function trigger() {
    setOpen(true);
    setLoading(true);
    setError(null);
    setSuggestion(null);
    try {
      const { data } = await apiClient.post<Suggestion>('/lookup', {
        entity,
        field,
        context,
      });
      setSuggestion(data);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 402) setError(t('suggest.error_no_credits'));
        else if (status === 403) setError(t('suggest.error_disabled'));
        else if (status === 404) setError(t('suggest.error_unsupported'));
        else setError(t('suggest.error_generic'));
      } else {
        setError(t('suggest.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  }

  function accept() {
    if (suggestion?.value) onAccept(suggestion.value);
    close();
  }

  function close() {
    setOpen(false);
    setSuggestion(null);
    setError(null);
  }

  return (
    <span className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={trigger}
        disabled={loading}
        title={t('suggest.tooltip')}
        aria-label={t('suggest.tooltip')}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          emphasize && 'border-primary/50 text-primary',
          loading && 'opacity-50',
        )}
      >
        <Sparkles className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full z-50 mt-1 w-80 rounded-md border bg-popover p-3 text-sm shadow-md"
        >
          {loading && (
            <div className="text-muted-foreground">{t('suggest.loading')}</div>
          )}
          {error && (
            <div className="text-destructive">{error}</div>
          )}
          {suggestion && !suggestion.value && (
            <div className="space-y-2">
              <div className="text-muted-foreground">
                {t('suggest.no_result')}
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={close}>
                  {t('common.close')}
                </Button>
              </div>
            </div>
          )}
          {suggestion?.value && (
            <div className="space-y-2">
              <div>
                <div className="text-xs text-muted-foreground">
                  {t('suggest.suggested_value')}
                </div>
                <div className="break-words font-medium">{suggestion.value}</div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {t('suggest.confidence')}:{' '}
                  <span className="font-medium">{suggestion.confidence}%</span>
                  {suggestion.cached && ` · ${t('suggest.cached')}`}
                </span>
              </div>
              {suggestion.rationale && (
                <div className="rounded bg-muted/50 px-2 py-1 text-xs">
                  {suggestion.rationale}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={close}>
                  {t('suggest.dismiss')}
                </Button>
                <Button size="sm" onClick={accept}>
                  {t('suggest.accept')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  );
}
